<template>
  <div v-if="showTabs" class="mb-6">
    <div class="backdrop-blur-sm py-3 px-3 glass-card-lite">
      <div class="flex items-center">
        <!-- Prev button -->
        <button
          type="button"
          @click="scrollByPage(-1)"
          :disabled="!canScrollPrev"
          aria-label="Scroll tabs left"
          class="group bg-transparent hover:bg-transparent p-1 disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none transition-opacity duration-200"
        >
          <ChevronLeft class="h-6 w-6 text-gray-900 dark:text-white transition-all duration-200 group-hover:text-fuchsia-500 group-hover:-translate-x-1 group-active:scale-90" />
        </button>

        <!-- Tabs -->
        <nav
          ref="tabNav"
          @scroll="onScroll"
          class="-mb-px flex space-x-8 overflow-x-auto flex-1 no-scrollbar scroll-smooth"
        >
          <button
            v-for="org in organizations"
            :key="org.id"
            @click="$emit('select', org.id)"
            :class="[
              'bg-transparent px-4 py-2 font-bold transition-all duration-200 flex items-center space-x-2 rounded-md border border-white glass-card whitespace-nowrap',
              selectedOrgId === org.id
                  ? 'text-fuchsia-500'
                  : 'text-gray-400 hover:bg-slate-800/30 hover:text-fuchsia-500'
              ]"
          >
            <!-- Pulsing dot -->
            <span v-if="org.has_pending_changes" class="absolute top-1 right-0 flex h-2 w-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 dark:bg-orange-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-yellow-500 dark:bg-orange-500"></span>
            </span>

            {{ org.name }}
          </button>
        </nav>

        <!-- Next button -->
        <button
          type="button"
          @click="scrollByPage(1)"
          :disabled="!canScrollNext"
          aria-label="Scroll tabs right"
          class="group bg-transparent hover:bg-transparent p-1 disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none transition-opacity duration-200"
        >
          <ChevronRight class="h-6 w-6 text-gray-900 dark:text-white transition-all duration-200 group-hover:text-fuchsia-500 group-hover:translate-x-1 group-active:scale-90" />
        </button>
      </div>

    </div>

    <!-- Scroll markers -->
    <div v-if="markerCount > 1" class="flex justify-center items-center gap-1.5 mt-2" aria-hidden="true">
      <span
        v-for="idx in markerCount"
        :key="idx"
        :class="[
          'scroll-marker',
          currentMarker === idx - 1 ? 'scroll-marker--active' : ''
        ]"
      ></span>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      <span class="ml-3 text-gray-600 dark:text-gray-400">{{ loadingText }}</span>
    </div>
  </div>
</template>

<script>
import { ChevronUpDownIcon } from '@heroicons/vue/24/outline';
import { ChevronLeft, ChevronRight, ArrowBigLeft, ArrowBigRight } from 'lucide-vue-next';
export default {
  name: 'OrganizationTabs',
  components: { ChevronLeft, ChevronRight, ArrowBigLeft, ArrowBigRight },
  props: {
    organizations: { type: Array, required: true },
    selectedOrgId: { type: Number, default: null },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: 'Loading...' },
    showTabs: { type: Boolean, default: false }
  },
  emits: ['select'],
  data() {
    return {
      scrollLeft: 0,
      scrollWidth: 0,
      clientWidth: 0
    };
  },
  computed: {
    canScrollPrev() {
      return this.scrollLeft > 1;
    },
    canScrollNext() {
      return this.scrollLeft + this.clientWidth < this.scrollWidth - 1;
    },
    markerCount() {
      if (!this.clientWidth || !this.scrollWidth) return 0;
      return Math.max(1, Math.ceil(this.scrollWidth / this.clientWidth));
    },
    currentMarker() {
      const maxScroll = this.scrollWidth - this.clientWidth;
      if (maxScroll <= 0 || this.markerCount <= 1) return 0;
      const ratio = Math.min(1, Math.max(0, this.scrollLeft / maxScroll));
      return Math.round(ratio * (this.markerCount - 1));
    }
  },
  mounted() {
    this.$nextTick(this.setup);
    window.addEventListener('resize', this.measure);
  },
  beforeUnmount() {
    this.teardown();
    window.removeEventListener('resize', this.measure);
  },
  updated() {
    this.$nextTick(this.setup);
  },
  watch: {
    showTabs() {
      this.$nextTick(this.setup);
    },
    organizations: {
      handler() {
        this.$nextTick(this.setup);
      },
      deep: false
    }
  },
  methods: {
    setup() {
      const el = this.$refs.tabNav;
      if (!el) return;
      if (!this._resizeObserver) {
        this._resizeObserver = new ResizeObserver(() => this.measure());
      }
      if (this._observedEl !== el) {
        if (this._observedEl) this._resizeObserver.unobserve(this._observedEl);
        this._resizeObserver.observe(el);
        this._observedEl = el;
      }
      this.measure();
    },
    teardown() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
        this._observedEl = null;
      }
    },
    measure() {
      const el = this.$refs.tabNav;
      if (!el) return;
      this.scrollLeft = el.scrollLeft;
      this.scrollWidth = el.scrollWidth;
      this.clientWidth = el.clientWidth;
    },
    onScroll() {
      const el = this.$refs.tabNav;
      if (!el) return;
      this.scrollLeft = el.scrollLeft;
    },
    scrollByPage(direction) {
      const el = this.$refs.tabNav;
      if (!el) return;
      el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: 'smooth' });
    }
  }
};
</script>

<style scoped>
.no-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.scroll-marker {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background-color: rgba(255, 255, 255, 0.5);
  transition: background-color 0.2s, width 0.2s, height 0.2s;
}
.scroll-marker--active {
  width: 8px;
  height: 8px;
  background-color: #d946ef;
}
</style>
